// src/components/desk/contract/BinderStates.jsx — driving a binder through its two machines.
//
// Migrations 1099–1105, owner rulings 2026-09-12. A binder has TWO states, not one:
//
//   the AGREEMENT   draft → signed → funded → active → completed
//                                                    | payer_default | owner_default
//                                                    | neutral_failure | mutually_cancelled
//   the MONEY       none → received → allocated → credited | refunded | retained
//
// Owner: "agreement signed and money received are not the same event." A binder spends its first
// hours signed-and-unpaid, and the old single `status` had no word for that — nor for a deal that
// failed through nobody's fault, which he called the biggest hole in the contract.
//
// 🔑 THIS PANEL DECIDES NOTHING. Every rule — forward only, final is final, a reservation
// activates only when fully funded, a refund reason that cannot contradict the outcome — is in
// `advance_binder` (1105). Here the moves are OFFERED from the vocabulary rows and the gateway is
// allowed to refuse; a refusal is shown as the sentence the gateway wrote. The temptation is to
// grey out what looks illegal, and that is exactly how a second definition of the rules gets
// written: it would be right today and wrong the day the machine changes, and it would be wrong
// SILENTLY, because a disabled button explains nothing.
//   What IS filtered here is only what the vocabulary itself says: sort_order for direction, and
// `is_final` for whether anything may follow. Both come from the rows, neither is a rule of ours.
//
// ⚠️ The states are ROWS (1100), so the labels come from `tOr(code, note)` — the 0427 lesson: t()
//    renders the raw KEY when a translation is missing, so a state added tomorrow shows its
//    English note rather than a slug nobody can read.
// ⚠️ `counts_as_failure` is READ from the refund reasons, never re-derived. It is the audit rule
//    that keeps a currency settlement — a COMPLETED deal whose money went back so it could be
//    re-paid in the right currency — out of the failure figures.

import { useState } from 'react'
import { t, tOr } from '../../../lib/i18n.js'
import { advanceBinder } from '../../../db/core.js'

const BTN = 'text-[12px] px-2 py-1 rounded border transition-colors disabled:opacity-40'
const PILL = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium'

// The label for a vocabulary row: the translation when we have one, its own note when we do not.
const nameOf = (row, prefix) => (row ? tOr(`${prefix}_${row.code}`, row.note || row.code) : '—')

export function BinderStates({ binder, vocab, onChanged, canAct = true }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [pending, setPending] = useState(null)   // the move being confirmed: {agreementState?, moneyState?}
  const [reason, setReason] = useState('')
  const [refundReason, setRefundReason] = useState('')

  const aStates = vocab?.agreement_states || []
  const mStates = vocab?.money_states || []
  const reasons = vocab?.refund_reasons || []
  if (!binder?.id || !aStates.length) return null

  const aNow = aStates.find(x => x.code === (binder.agreement_state || 'draft')) || aStates[0]
  const mNow = mStates.find(x => x.code === (binder.money_state || 'none')) || mStates[0]

  // Everything the vocabulary says is still ahead. Not "everything legal" — the gateway owns that.
  const aNext = aNow?.is_final ? [] : aStates.filter(x => x.sort_order > aNow.sort_order)
  const mNext = mNow?.is_final ? [] : mStates.filter(x => x.sort_order > mNow.sort_order)

  // A failure ending needs a reason, and a refund needs to say why — both are gateway rules,
  // asked for HERE only so the agent is not made to discover them through an error message.
  const targetA = pending?.agreementState ? aStates.find(x => x.code === pending.agreementState) : null
  const needsReason = !!targetA?.is_failure
  const needsRefundReason = pending?.moneyState === 'refunded'
  const chosenReason = reasons.find(r => r.code === refundReason)

  const start = move => { setPending(move); setReason(''); setRefundReason(''); setErr('') }
  const cancel = () => { setPending(null); setReason(''); setRefundReason(''); setErr('') }

  async function commit() {
    setBusy(true); setErr('')
    try {
      const r = await advanceBinder(binder.id, { ...pending, reason, refundReason })
      cancel()
      onChanged?.(r?.binder || null)
    } catch (e) {
      // The gateway's own sentence, not a paraphrase: it names the rule that refused.
      setErr(String(e?.message || e) || t('desk_binder_move_failed'))
    } finally { setBusy(false) }
  }

  const blocked = (needsReason && !reason.trim()) || (needsRefundReason && !refundReason)

  return (
    <div className="space-y-2" data-testid="binder-states">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[11px] text-white/50">{t('desk_binder_agreement_state')}</span>
        <span className={`${PILL} bg-white/10 text-white/90`} data-agreement-state={aNow?.code}>{nameOf(aNow, 'binder_astate')}</span>
        <span className="text-[11px] text-white/50">{t('desk_binder_money_state')}</span>
        <span className={`${PILL} bg-white/10 text-white/90`} data-money-state={mNow?.code}>{nameOf(mNow, 'binder_mstate')}</span>
        {binder.refund_reason && (
          <span className={`${PILL} bg-white/5 text-white/70`} data-refund-reason={binder.refund_reason}>
            {nameOf(reasons.find(r => r.code === binder.refund_reason), 'binder_refund')}
          </span>
        )}
      </div>

      {canAct && !pending && (aNext.length > 0 || mNext.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {aNext.map(s => (
            <button key={`a:${s.code}`} className={`${BTN} border-white/25 text-white/80 hover:bg-white/10`}
              data-move-agreement={s.code} disabled={busy} onClick={() => start({ agreementState: s.code })}>
              {nameOf(s, 'binder_astate')}
            </button>
          ))}
          {mNext.map(s => (
            <button key={`m:${s.code}`} className={`${BTN} border-[#1F6F4F]/60 text-[#8FD3B4] hover:bg-[#1F6F4F]/20`}
              data-move-money={s.code} disabled={busy} onClick={() => start({ moneyState: s.code })}>
              {nameOf(s, 'binder_mstate')}
            </button>
          ))}
        </div>
      )}

      {pending && (
        <div className="rounded-lg border border-white/20 p-2 space-y-2" data-testid="binder-move-confirm">
          <div className="text-[12px] text-white/85">
            {t('desk_binder_move_to')}{' '}
            <b>{pending.agreementState
              ? nameOf(aStates.find(x => x.code === pending.agreementState), 'binder_astate')
              : nameOf(mStates.find(x => x.code === pending.moneyState), 'binder_mstate')}</b>
          </div>

          {needsRefundReason && (
            <div className="space-y-1">
              <label className="text-[11px] text-white/60">{t('desk_binder_refund_reason')}</label>
              <select className="w-full bg-white/5 border border-white/20 rounded px-1.5 py-1 text-[16px] sm:text-[12px] text-white/90 min-w-0"
                value={refundReason} data-refund-pick onChange={e => setRefundReason(e.target.value)}>
                <option value="">—</option>
                {reasons.map(r => <option key={r.code} value={r.code}>{nameOf(r, 'binder_refund')}</option>)}
              </select>
              {/* The audit distinction, said out loud at the moment it is chosen — the owner's
                  reason for keeping the reason separate from the state at all. */}
              {chosenReason && (
                <div className="text-[11px] text-white/55" data-refund-note>
                  {chosenReason.counts_as_failure ? t('desk_binder_counts_failure') : t('desk_binder_not_failure')}
                </div>
              )}
            </div>
          )}

          {needsReason && (
            <div className="space-y-1">
              <label className="text-[11px] text-white/60">{t('desk_binder_why')}</label>
              <input className="w-full bg-white/5 border border-white/20 rounded px-1.5 py-1 text-[16px] sm:text-[12px] text-white/90 min-w-0"
                value={reason} data-move-reason onChange={e => setReason(e.target.value)} />
            </div>
          )}

          {err && <div className="text-[12px] text-red-300" data-move-error>{err}</div>}

          <div className="flex gap-1.5">
            <button className={`${BTN} border-[#1F6F4F] bg-[#1F6F4F] text-white`} disabled={busy || blocked}
              data-move-commit onClick={commit}>{busy ? t('desk_binder_moving') : t('desk_binder_confirm')}</button>
            <button className={`${BTN} border-white/25 text-white/70`} disabled={busy} data-move-cancel onClick={cancel}>
              {t('desk_binder_cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
